import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, User, Brain, Bot, Menu, X, BookOpen, Share2, Plus, History, BarChart3, Code, Globe, ChevronDown, ChevronRight, TrendingUp, Settings, Star, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from './MainLayout';
import './ThemePreview.css';

// Custom Brain Icon Component using PNG
const BrainIcon = ({ size = 64, className = "" }) => (
  <img 
    src={process.env.PUBLIC_URL + "/brain-logo.png"} 
    alt="Agent You Logo" 
    width={size} 
    height={size} 
    className={className}
  />
);

const ThemePreview = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [activeSection, setActiveSection] = useState('discover');
  const [activeItem, setActiveItem] = useState('discover');

  // Mock data for theme preview - 50 agents
  const mockAgents = [
    // Featured Agents (5)
    {
      id: 1,
      name: 'My Agent',
      description: 'AI agent built with Agent Builder',
      agent_avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiM2NjdlZWEiLz4KPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5NPC90ZXh0Pgo8L3N2Zz4K',
      chat_count: 20,
      tags: ['AI', 'Assistant'],
      is_featured: true
    },
    {
      id: 2,
      name: 'URLinks',
      description: 'Let me tell ya about my app URLinks',
      agent_avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiM3NjRiYTIiLz4KPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyMCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5VPC90ZXh0Pgo8L3N2Zz4K',
      chat_count: 28,
      tags: ['Product', 'Tech'],
      is_featured: true
    },
    {
      id: 3,
      name: 'Writing Mentor',
      description: 'Expert writing coach with personalized feedback and style analysis',
      agent_avatar: null,
      chat_count: 156,
      tags: ['Writing', 'Education'],
      is_featured: true
    },
    {
      id: 4,
      name: 'Code Guru',
      description: 'Senior developer helping you master programming concepts',
      agent_avatar: null,
      chat_count: 89,
      tags: ['Programming', 'Tech'],
      is_featured: true
    },
    {
      id: 5,
      name: 'Design Critic',
      description: 'Professional designer providing UI/UX feedback and suggestions',
      agent_avatar: null,
      chat_count: 67,
      tags: ['Design', 'Creative'],
      is_featured: true
    },
    // Regular Agents (45)
    {
      id: 6,
      name: 'Math Tutor',
      description: 'Helps with calculus, algebra, and advanced mathematics',
      agent_avatar: null,
      chat_count: 34,
      tags: ['Education', 'Math'],
      is_featured: false
    },
    {
      id: 7,
      name: 'Language Partner',
      description: 'Practice conversation in Spanish, French, or German',
      agent_avatar: null,
      chat_count: 78,
      tags: ['Language', 'Education'],
      is_featured: false
    },
    {
      id: 8,
      name: 'Fitness Coach',
      description: 'Personal trainer providing workout plans and nutrition advice',
      agent_avatar: null,
      chat_count: 45,
      tags: ['Health', 'Fitness'],
      is_featured: false
    },
    {
      id: 9,
      name: 'Business Advisor',
      description: 'Strategic business consultant for startups and entrepreneurs',
      agent_avatar: null,
      chat_count: 92,
      tags: ['Business', 'Strategy'],
      is_featured: false
    },
    {
      id: 10,
      name: 'Creative Writer',
      description: 'Collaborative storytelling and creative writing partner',
      agent_avatar: null,
      chat_count: 23,
      tags: ['Writing', 'Creative'],
      is_featured: false
    },
    {
      id: 11,
      name: 'Data Analyst',
      description: 'Expert in data science, statistics, and machine learning',
      agent_avatar: null,
      chat_count: 67,
      tags: ['Data', 'Tech'],
      is_featured: false
    },
    {
      id: 12,
      name: 'Music Producer',
      description: 'Helps with music composition, mixing, and production',
      agent_avatar: null,
      chat_count: 31,
      tags: ['Music', 'Creative'],
      is_featured: false
    },
    {
      id: 13,
      name: 'Travel Planner',
      description: 'Expert travel advisor for destinations worldwide',
      agent_avatar: null,
      chat_count: 56,
      tags: ['Travel', 'Planning'],
      is_featured: false
    },
    {
      id: 14,
      name: 'Psychology Helper',
      description: 'Mental health support and psychological insights',
      agent_avatar: null,
      chat_count: 89,
      tags: ['Health', 'Psychology'],
      is_featured: false
    },
    {
      id: 15,
      name: 'Cooking Assistant',
      description: 'Recipe suggestions and cooking techniques for all skill levels',
      agent_avatar: null,
      chat_count: 42,
      tags: ['Food', 'Lifestyle'],
      is_featured: false
    },
    {
      id: 16,
      name: 'History Teacher',
      description: 'Expert in world history, ancient civilizations, and historical events',
      agent_avatar: null,
      chat_count: 28,
      tags: ['Education', 'History'],
      is_featured: false
    },
    {
      id: 17,
      name: 'Science Tutor',
      description: 'Physics, chemistry, and biology concepts explained simply',
      agent_avatar: null,
      chat_count: 73,
      tags: ['Education', 'Science'],
      is_featured: false
    },
    {
      id: 18,
      name: 'Art Critic',
      description: 'Art history, techniques, and creative inspiration',
      agent_avatar: null,
      chat_count: 19,
      tags: ['Art', 'Creative'],
      is_featured: false
    },
    {
      id: 19,
      name: 'Financial Planner',
      description: 'Investment advice, budgeting, and financial planning',
      agent_avatar: null,
      chat_count: 95,
      tags: ['Finance', 'Business'],
      is_featured: false
    },
    {
      id: 20,
      name: 'Philosophy Guide',
      description: 'Deep discussions on ethics, logic, and philosophical concepts',
      agent_avatar: null,
      chat_count: 37,
      tags: ['Philosophy', 'Education'],
      is_featured: false
    },
    {
      id: 21,
      name: 'Gaming Coach',
      description: 'Strategy guides and tips for popular video games',
      agent_avatar: null,
      chat_count: 64,
      tags: ['Gaming', 'Entertainment'],
      is_featured: false
    },
    {
      id: 22,
      name: 'Photography Mentor',
      description: 'Camera techniques, composition, and photo editing advice',
      agent_avatar: null,
      chat_count: 51,
      tags: ['Photography', 'Creative'],
      is_featured: false
    },
    {
      id: 23,
      name: 'Meditation Guide',
      description: 'Mindfulness practices and stress reduction techniques',
      agent_avatar: null,
      chat_count: 38,
      tags: ['Health', 'Wellness'],
      is_featured: false
    },
    {
      id: 24,
      name: 'Legal Advisor',
      description: 'General legal information and document assistance',
      agent_avatar: null,
      chat_count: 26,
      tags: ['Legal', 'Professional'],
      is_featured: false
    },
    {
      id: 25,
      name: 'Marketing Expert',
      description: 'Digital marketing strategies and brand development',
      agent_avatar: null,
      chat_count: 82,
      tags: ['Marketing', 'Business'],
      is_featured: false
    },
    {
      id: 26,
      name: 'Poetry Companion',
      description: 'Poetry writing, analysis, and literary appreciation',
      agent_avatar: null,
      chat_count: 14,
      tags: ['Writing', 'Literature'],
      is_featured: false
    },
    {
      id: 27,
      name: 'Astronomy Guide',
      description: 'Space exploration, celestial events, and astrophysics',
      agent_avatar: null,
      chat_count: 47,
      tags: ['Science', 'Space'],
      is_featured: false
    },
    {
      id: 28,
      name: 'Dance Instructor',
      description: 'Dance techniques, choreography, and movement therapy',
      agent_avatar: null,
      chat_count: 29,
      tags: ['Dance', 'Fitness'],
      is_featured: false
    },
    {
      id: 29,
      name: 'Gardening Helper',
      description: 'Plant care, garden design, and horticultural advice',
      agent_avatar: null,
      chat_count: 33,
      tags: ['Gardening', 'Lifestyle'],
      is_featured: false
    },
    {
      id: 30,
      name: 'Career Counselor',
      description: 'Job search strategies and career development guidance',
      agent_avatar: null,
      chat_count: 71,
      tags: ['Career', 'Professional'],
      is_featured: false
    },
    {
      id: 31,
      name: 'Chess Master',
      description: 'Chess strategies, tactics, and game analysis',
      agent_avatar: null,
      chat_count: 58,
      tags: ['Chess', 'Strategy'],
      is_featured: false
    },
    {
      id: 32,
      name: 'Fashion Stylist',
      description: 'Personal styling and fashion trend advice',
      agent_avatar: null,
      chat_count: 41,
      tags: ['Fashion', 'Style'],
      is_featured: false
    },
    {
      id: 33,
      name: 'Movie Critic',
      description: 'Film analysis, recommendations, and cinema history',
      agent_avatar: null,
      chat_count: 76,
      tags: ['Movies', 'Entertainment'],
      is_featured: false
    },
    {
      id: 34,
      name: 'Book Reviewer',
      description: 'Book recommendations and literary analysis',
      agent_avatar: null,
      chat_count: 63,
      tags: ['Books', 'Literature'],
      is_featured: false
    },
    {
      id: 35,
      name: 'Podcast Producer',
      description: 'Audio editing, content creation, and podcasting advice',
      agent_avatar: null,
      chat_count: 25,
      tags: ['Audio', 'Media'],
      is_featured: false
    },
    {
      id: 36,
      name: 'Yoga Instructor',
      description: 'Yoga poses, meditation, and mindfulness practices',
      agent_avatar: null,
      chat_count: 49,
      tags: ['Yoga', 'Wellness'],
      is_featured: false
    },
    {
      id: 37,
      name: 'Language Translator',
      description: 'Translation services and language learning support',
      agent_avatar: null,
      chat_count: 87,
      tags: ['Translation', 'Language'],
      is_featured: false
    },
    {
      id: 38,
      name: 'Event Planner',
      description: 'Wedding, party, and corporate event planning assistance',
      agent_avatar: null,
      chat_count: 35,
      tags: ['Events', 'Planning'],
      is_featured: false
    },
    {
      id: 39,
      name: 'Interior Designer',
      description: 'Home decor, space planning, and design inspiration',
      agent_avatar: null,
      chat_count: 52,
      tags: ['Design', 'Home'],
      is_featured: false
    },
    {
      id: 40,
      name: 'Pet Care Expert',
      description: 'Pet training, health advice, and animal behavior insights',
      agent_avatar: null,
      chat_count: 68,
      tags: ['Pets', 'Animals'],
      is_featured: false
    },
    {
      id: 41,
      name: 'Cryptocurrency Guide',
      description: 'Blockchain technology and digital currency education',
      agent_avatar: null,
      chat_count: 94,
      tags: ['Crypto', 'Finance'],
      is_featured: false
    },
    {
      id: 42,
      name: 'Sustainability Coach',
      description: 'Eco-friendly living and environmental conservation tips',
      agent_avatar: null,
      chat_count: 31,
      tags: ['Environment', 'Lifestyle'],
      is_featured: false
    },
    {
      id: 43,
      name: 'Public Speaker',
      description: 'Presentation skills and public speaking confidence building',
      agent_avatar: null,
      chat_count: 44,
      tags: ['Speaking', 'Professional'],
      is_featured: false
    },
    {
      id: 44,
      name: 'Time Management',
      description: 'Productivity techniques and schedule optimization',
      agent_avatar: null,
      chat_count: 57,
      tags: ['Productivity', 'Organization'],
      is_featured: false
    },
    {
      id: 45,
      name: 'Relationship Coach',
      description: 'Dating advice and relationship counseling',
      agent_avatar: null,
      chat_count: 72,
      tags: ['Relationships', 'Dating'],
      is_featured: false
    },
    {
      id: 46,
      name: 'Parenting Helper',
      description: 'Child development and parenting strategies',
      agent_avatar: null,
      chat_count: 39,
      tags: ['Parenting', 'Family'],
      is_featured: false
    },
    {
      id: 47,
      name: 'DIY Expert',
      description: 'Home improvement projects and repair tutorials',
      agent_avatar: null,
      chat_count: 46,
      tags: ['DIY', 'Home'],
      is_featured: false
    },
    {
      id: 48,
      name: 'Memory Trainer',
      description: 'Memory techniques and cognitive enhancement exercises',
      agent_avatar: null,
      chat_count: 22,
      tags: ['Memory', 'Learning'],
      is_featured: false
    },
    {
      id: 49,
      name: 'Negotiation Coach',
      description: 'Negotiation strategies and conflict resolution skills',
      agent_avatar: null,
      chat_count: 18,
      tags: ['Negotiation', 'Professional'],
      is_featured: false
    },
    {
      id: 50,
      name: 'Innovation Catalyst',
      description: 'Creative problem-solving and innovation techniques',
      agent_avatar: null,
      chat_count: 36,
      tags: ['Innovation', 'Creative'],
      is_featured: false
    }
  ];

  const categories = [
    { id: 'all', name: 'All Agents' },
    { id: 'writing', name: 'Writing' },
    { id: 'coding', name: 'Coding' },
    { id: 'general', name: 'General' }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setAgents(mockAgents);
      setLoading(false);
    }, 500);
  }, []);

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           agent.description.toLowerCase().includes(selectedCategory.toLowerCase());
    return matchesSearch && matchesCategory;
  });

  const featuredAgents = filteredAgents.filter(agent => agent.is_featured);
  const regularAgents = filteredAgents.filter(agent => !agent.is_featured);

  const handleChatClick = (agent) => {
    console.log('Chat clicked for:', agent.name);
    // In real app, this would navigate to chat
  };

  if (loading) {
    return (
      <div className="theme-preview">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading theme preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-preview">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">
            <BrainIcon className="title-icon" />
            Agent You
          </h1>
          <p className="app-subtitle">Don't just use a tool, be a tool!</p>
          <div className={`mode-toggle ${activeSection === 'discover' ? 'discover-mode' : ''}`}>
            <button 
              className={`toggle-button ${activeSection === 'build' ? 'active' : ''}`}
              onClick={() => setActiveSection(activeSection === 'build' ? 'discover' : 'build')}
            >
              Build
            </button>
            <button 
              className={`toggle-button ${activeSection === 'discover' ? 'active' : ''}`}
              onClick={() => setActiveSection(activeSection === 'build' ? 'discover' : 'build')}
            >
              Discover
            </button>
          </div>
          <div className="user-section">
            <div className="user-info" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              <span className="user-name">{user?.name || 'User'}</span>
              <span className="user-email">{user?.email || 'user@example.com'}</span>
            </div>
            <div className="user-avatar" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              {user?.picture ? (
                <img src={user.picture} alt={user.name} />
              ) : (
                <User className="default-avatar" />
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="theme-layout">
        {/* Themed Sidebar - Discover Page */}
        <div className="theme-sidebar">
          <nav className="sidebar-nav">
            <div className="nav-section">
              <button className="section-header active">
                <Bot size={20} />
                <span>Discover Agents</span>
                <ChevronDown size={16} className="chevron" />
              </button>
              <div className="section-items">
                <button className="nav-item active">
                  <Search size={16} />
                  <div className="item-content">
                    <span className="item-title">Browse All</span>
                  </div>
                </button>
                <button className="nav-item">
                  <TrendingUp size={16} />
                  <div className="item-content">
                    <span className="item-title">Trending</span>
                  </div>
                </button>
                <button className="nav-item">
                  <Star size={16} />
                  <div className="item-content">
                    <span className="item-title">Featured</span>
                  </div>
                </button>
                <button className="nav-item">
                  <Clock size={16} />
                  <div className="item-content">
                    <span className="item-title">Recent</span>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="nav-section">
              <button className="section-header">
                <MessageCircle size={20} />
                <span>My Conversations</span>
                <ChevronRight size={16} className="chevron" />
              </button>
            </div>
            
            <div className="nav-section">
              <button className="section-header">
                <Settings size={20} />
                <span>Settings</span>
                <ChevronRight size={16} className="chevron" />
              </button>
            </div>
          </nav>
        </div>

        <div className="theme-content">
          {/* Page Header */}
          <div className="theme-header">
            <h1 className="theme-title">Discover Agents</h1>
            <p className="theme-subtitle">Find and chat with AI agents created by other users</p>
            
            {/* Search Bar */}
            <div className="search-bar">
              <div className="search-container">
                <Search className="search-icon" size={20} />
                <input
                  type="text"
                  placeholder="Search agents by name, description, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
          </div>

      {/* Featured Agents Section */}
      {featuredAgents.length > 0 && (
        <div className="featured-section">
          <h2 className="section-title">Featured Agents</h2>
          <div className="featured-grid">
            {featuredAgents.map(agent => (
              <AgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
            ))}
          </div>
        </div>
      )}

      {/* All Agents Section */}
      <div className="all-agents-section">
        <h2 className="section-title">
          All Agents ({filteredAgents.length})
        </h2>
        <div className="agents-grid">
          {regularAgents.map(agent => (
            <AgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
          ))}
        </div>
        {filteredAgents.length === 0 && (
          <div className="no-results">
            <Search size={48} />
            <h3>No agents found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

const AgentCard = ({ agent, onChatClick }) => {
  return (
    <div className="agent-card">
      <div className="agent-header">
        <div className="agent-avatar">
          {agent.agent_avatar ? (
            <img 
              src={agent.agent_avatar} 
              alt={agent.name}
            />
          ) : (
            <User size={20} />
          )}
        </div>
        <div className="agent-stats">
          <MessageCircle size={16} />
          <span>{agent.chat_count} chats</span>
        </div>
      </div>
      
      <div className="agent-content">
        <h3 className="agent-name">{agent.name}</h3>
        <p className="agent-description">{agent.description}</p>
        
        <div className="agent-tags">
          {agent.tags.map(tag => (
            <span key={tag} className="tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
      
      <div className="agent-footer">
        <button 
          className="chat-button"
          onClick={() => onChatClick(agent)}
        >
          <MessageCircle size={16} />
          Chat
        </button>
      </div>
    </div>
  );
};

export default ThemePreview;
